import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUp, Check, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { validatePrompt, startCheckout, fetchJob, fetchEngines, fetchTiers, getPreferredEngine, setPreferredEngine } from "@/lib/api";
import { rememberJob, activeJob, clearActive, isActive } from "@/lib/jobs";
import { getTurnstileToken } from "@/lib/turnstile";
import { readyText } from "@/lib/brand";

/**
 * TRAIN AN EXPERT — the site's one job beyond the download (2026-10-27 rebuild of the old
 * terminal-style "bar"). Same flow, same endpoints, same rulings:
 *   · the subject is screened first (/api/screening/validate);
 *   · the person picks the Engine on their phone (L63) and how far the run reads (the
 *     tiers, L190 — by scope, never by method; prices are the hub's, at the web door);
 *   · checkout is Stripe at the hub's price; no public free training (L196).
 * The microphone is gone: the browser's speech recognition sends audio to a cloud service,
 * which this product promises never to do.
 */
export default function TrainPanel({ compact = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const [subject, setSubject] = useState("");
  const [phase, setPhase] = useState("idle");     // idle | checking | accepted | refine | refused
  const [verdict, setVerdict] = useState(null);
  const [engines, setEngines] = useState([]);
  const [engineId, setEngineId] = useState(getPreferredEngine());
  const [tiers, setTiers] = useState([]);
  const [scope, setScope] = useState("standard");
  const [paying, setPaying] = useState(false);
  const [running, setRunning] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const id = activeJob();
    if (!id) return;
    fetchJob(id).then((j) => { if (isActive(j)) setRunning(j); else clearActive(id); }).catch(() => {});
  }, []);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) { setDraft(prefill); navigate(".", { replace: true, state: {} }); }
  }, [location.state, navigate]);

  useEffect(() => {
    fetchEngines().then((d) => {
      const list = d?.engines || [];
      setEngines(list);
      setEngineId((cur) => (list.some((e) => e.engine_id === cur) ? cur : (list[0]?.engine_id || "")));
    }).catch(() => setEngines([]));
  }, []);

  const loadTiers = () => fetchTiers().then((d) => setTiers(d?.tiers || [])).catch(() => setTiers([]));
  useEffect(() => { loadTiers(); }, []);

  const chosen = tiers.find((t) => t.scope === scope && t.available) || tiers.find((t) => t.available);

  const submit = async () => {
    const val = draft.trim();
    if (!val || phase === "checking") return;
    setSubject(val);
    setPhase("checking");
    setVerdict(null);
    try {
      let token = null;
      try { token = await getTurnstileToken(); } catch (_) { /* disabled, or the visitor closed it */ }
      const v = await validatePrompt(val, token);
      setVerdict(v);
      setPhase(v.verdict === "accept" ? "accepted" : v.verdict === "refine" ? "refine" : "refused");
      if (v.verdict === "accept") loadTiers();
    } catch (e) {
      setPhase("idle");
      const msg = e?.response?.data?.detail;
      toast.error(msg && /verification/i.test(String(msg)) ? msg : "We couldn't check that subject just now. Please try again in a moment.");
    }
  };

  const reset = () => {
    setPhase("idle"); setVerdict(null); setSubject(""); setPaying(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const pay = async () => {
    if (!chosen || paying) return;
    setPaying(true);
    try {
      const res = await startCheckout({
        prompt: subject,
        origin_url: window.location.origin,
        engine_id: engineId,
        scope: chosen.scope,
      });
      rememberJob(res.job_id, res.recovery_key);
      if (res.checkout_url) window.location.href = res.checkout_url;
      else { setPaying(false); toast.error("Checkout didn't open. Please try again."); }
    } catch (e) {
      setPaying(false);
      toast.error(e?.response?.data?.detail || "Checkout didn't open. Please try again.");
    }
  };

  const chooseEngine = (id) => { setEngineId(id); setPreferredEngine(id); };

  return (
    <div className="w-full" data-testid="train-panel">
      {running && phase === "idle" && (
        <Link to={`/progress/${running.job_id}`} className="ex-card ex-card-link flex items-center gap-4 p-5 mb-5" data-testid="running-job">
          <span className="ex-dot ex-dot-live" />
          <span className="min-w-0">
            <span className="block text-white truncate">Training · {running.subject}</span>
            <span className="block ex-small truncate">{running.progress}</span>
          </span>
          <ArrowRight className="ml-auto h-4 w-4 text-white flex-none" strokeWidth={1.8} />
        </Link>
      )}

      {/* the composer */}
      {(phase === "idle" || phase === "checking") && (
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="ex-field flex items-end gap-2 p-2 pl-5"
        >
          <textarea
            ref={inputRef}
            data-testid="input"
            value={phase === "checking" ? subject : draft}
            disabled={phase === "checking"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder="Name a subject — the more specific, the better"
            rows={compact ? 1 : 2}
            maxLength={200}
            className="ex-input py-3 max-h-40"
            aria-label="Subject to train an Expert on"
          />
          {/* L215: one limit everywhere (the hub trains the first 200 characters); say so. */}
          {draft.length >= 150 && phase === "idle" && (
            <span className="ex-tiny self-center flex-none" aria-live="polite">{draft.length}/200</span>
          )}
          <button
            type="submit" data-testid="send-btn" aria-label="Check this subject"
            disabled={!draft.trim() || phase === "checking"}
            className="ex-btn ex-btn-primary h-11 w-11 p-0 flex-none"
          >
            {phase === "checking" ? <span className="ex-spinner" style={{ borderTopColor: "#0b0b0c" }} /> : <ArrowUp className="h-5 w-5" strokeWidth={2} />}
          </button>
        </form>
      )}
      {phase === "idle" && (
        <div className="flex flex-wrap gap-2 mt-4">
          {["Small-engine repair for lawn mowers", "Sourdough bread baking", "Raising backyard chickens", "Residential electrical wiring"].map((s) => (
            <button key={s} type="button" onClick={() => { setDraft(s); inputRef.current?.focus(); }} className="ex-chip hover:text-white hover:border-[color:var(--ex-line2)] transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}

      {phase === "checking" && (
        <div className="ex-panel p-5 mt-4 ex-trail" data-testid="checking">
          <Step state="active">Reading your subject</Step>
          <Step state="wait">Screening it against our content rules</Step>
          <Step state="wait">Planning where to find the best sources</Step>
        </div>
      )}

      {(phase === "refine" || phase === "refused") && (
        <div className="ex-panel p-6" data-testid="verdict">
          <div className="ex-small mb-2">“{subject}”</div>
          <div className="text-white text-[17px] leading-relaxed">
            {verdict?.message || (phase === "refine" ? "That subject needs to be a little more specific." : "We can't train an Expert on that subject.")}
          </div>
          <button type="button" onClick={reset} className="ex-btn ex-btn-secondary ex-btn-sm mt-6" data-testid="reset-btn">
            {phase === "refine" ? "Refine the subject" : "Try a different subject"}
          </button>
        </div>
      )}

      {phase === "accepted" && (
        <div className="space-y-5" data-testid="accepted">
          <div className="ex-panel p-6">
            <div className="ex-trail">
              <Step state="done">Subject read</Step>
              <Step state="done">Screened — nothing in the way</Step>
              <Step state="done">Sources are found during the run</Step>
            </div>
            <div className="mt-5 pt-5 border-t border-[color:var(--ex-line)] flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="ex-tiny">Your Expert</div>
                <div className="text-white text-[18px] font-medium truncate">{subject}</div>
              </div>
              <button type="button" onClick={reset} className="ex-btn ex-btn-quiet ex-btn-sm">Change</button>
            </div>
          </div>

          {engines.length > 0 && (
            <div>
              <div className="ex-eyebrow mb-3">Your phone's Engine</div>
              <div className="grid gap-3 sm:grid-cols-2">
                {engines.map((e) => (
                  <button key={e.engine_id} type="button" className="ex-option flex items-start gap-4" aria-pressed={engineId === e.engine_id}
                    onClick={() => chooseEngine(e.engine_id)} data-testid={`engine-${e.engine_id}`}>
                    <span className="ex-radio mt-0.5" />
                    <span className="min-w-0">
                      <span className="block font-medium">{e.display}</span>
                      {e.blurb && <span className="block ex-small mt-1">{e.blurb}</span>}
                    </span>
                  </button>
                ))}
              </div>
              <p className="ex-tiny mt-3">An Expert runs only on the Engine it was trained for. The app shows yours at the top of the screen.</p>
            </div>
          )}

          <div>
            <div className="ex-eyebrow mb-3">How far it reads</div>
            {tiers.length === 0 ? (
              <div className="ex-small">Loading prices…</div>
            ) : (
              <div className="grid gap-3">
                {tiers.map((t) => (
                  <button key={t.scope} type="button" disabled={!t.available} className="ex-option" aria-pressed={chosen?.scope === t.scope}
                    onClick={() => setScope(t.scope)} data-testid={`tier-${t.scope}`}>
                    <span className="flex items-start gap-4">
                      <span className="ex-radio mt-1" />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                          <span className="font-medium text-[16px]">{t.label}</span>
                          <span className="text-[16px] font-medium">{t.available ? t.price_text : "Coming soon"}</span>
                        </span>
                        <span className="block ex-small mt-1.5">{t.contains}</span>
                        {chosen?.scope === t.scope && (t.includes || []).length > 0 && (
                          <span className="block mt-3 space-y-1.5">
                            {t.includes.map((line) => (
                              <span key={line} className="flex gap-2.5 ex-small text-left">
                                <Check className="h-4 w-4 flex-none mt-[3px] text-white" strokeWidth={2} /> <span>{line}</span>
                              </span>
                            ))}
                          </span>
                        )}
                        {t.available && t.ready_in_minutes > 0 && (
                          <span className="block ex-tiny mt-2">Ready in {readyText(t.ready_in_minutes).toLowerCase()}</span>
                        )}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pt-1">
            <button type="button" onClick={pay} disabled={!chosen || paying} className="ex-btn ex-btn-primary ex-btn-block h-14 text-[16px]" data-testid="forge-btn">
              {paying ? <span className="ex-spinner" style={{ borderTopColor: "#0b0b0c" }} /> : <Lock className="h-4 w-4" strokeWidth={2} />}
              {chosen ? `Continue to secure checkout · ${chosen.price_text}` : "Loading prices…"}
            </button>
            <p className="ex-tiny mt-3 text-center">
              Payment by Stripe. Any sales tax is added at checkout. Every tier gets the same fact checks and is tested on your Engine before it reaches you; the higher tiers read more sources and are examined longer.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ state, children }) {
  return (
    <div className="ex-trail-row" style={{ color: state === "wait" ? "var(--ex-faint)" : undefined }}>
      <span className="ex-trail-icon">
        {state === "done" && <Check className="h-4 w-4 text-white" strokeWidth={2.2} />}
        {state === "active" && <span className="ex-spinner" />}
        {state === "wait" && <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--ex-line2)]" />}
      </span>
      <span className={state === "done" ? "text-white" : ""}>{children}</span>
    </div>
  );
}
